import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';
import { Setting } from '../../models';
import ModalComponent from '../ModalComponent/ModalComponent';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import SortableList from '../../containers/EmailLayouts/LayoutsList/EmailLayouts/SortableList/SortableList';
import EmailLayoutPreviewChrome from '../../containers/EmailLayouts/LayoutsList/EmailLayouts/EmailLayoutPreviewChrome/EmailLayoutPreviewChrome';
import { Utils } from '../../services';
import { buildDefaultLayoutEmailPreviewContext } from '../../services/defaultLayoutEmailPreviewUtils';
import {
    is_distributor_report_email_layout,
    DISTRIBUTOR_REPORT_LANGS,
} from '../../services/distributorReportPreview';

import '../../containers/EmailLayouts/LayoutsList/EmailLayouts/EmailLayouts.scss';

/**
 * Layout content frame styles from options.style (same as EmailLayouts editor).
 *
 * @param {object|null} layout
 * @returns {object}
 */
function content_wrapper_style_from_layout(layout) {
    const style = layout?.options?.style ?? {};
    const wrapper = {};
    const background = style.backgroundColor;
    const border_width = Number(style.borderWidth) || 0;
    const border_style = style.borderStyle || 'solid';
    const border_color = style.borderColor;
    const border_radius = Number(style.borderRadius) || 0;

    if (background)
        wrapper.backgroundColor = background;
    if (border_width > 0 && border_color)
        wrapper.border = `${border_width}px ${border_style} ${border_color}`;
    if (border_radius > 0) {
        wrapper.borderRadius = `${border_radius}px`;
        wrapper.overflow = 'hidden';
        wrapper.backgroundClip = 'padding-box';
    }

    return wrapper;
}

/**
 * Preview from "default layouts" settings: list endpoints omit widgets/subject
 * (listing fields only), so we load the full layout document when opening.
 * Renders inside `.EmailLayouts` so SortableList / previewWrapper CSS match the editor.
 */
const EmailLayoutPreviewButton = ({ layoutId, layoutKey, emailLayouts, setting, lang }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [previewLang, setPreviewLang] = useState(lang || Utils.default_lang());
    const [fullLayout, setFullLayout] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const listLayout = useMemo(
        () => (emailLayouts || []).find(item => item._id === layoutId) || null,
        [emailLayouts, layoutId]
    );

    const layout = fullLayout || listLayout;

    const previewSetting = useMemo(() => {
        if (!setting)
            return null;

        return setting instanceof Setting ? setting : new Setting(setting);
    }, [setting]);

    const previewContext = useMemo(() => {
        if (!layout || !previewSetting)
            return null;

        return buildDefaultLayoutEmailPreviewContext(layoutKey, previewSetting, previewLang);
    }, [layout, layoutKey, previewSetting, previewLang]);

    const contentWrapperStyle = useMemo(
        () => content_wrapper_style_from_layout(layout),
        [layout]
    );

    const layoutPreviewLanguages = useMemo(() => {
        if (!layout)
            return undefined;

        if (is_distributor_report_email_layout(layout, setting))
            return DISTRIBUTOR_REPORT_LANGS;

        return undefined;
    }, [layout, setting]);

    useEffect(() => {
        if (!open || !layoutId) {
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setFullLayout(null);

        (async () => {
            try {
                const { default: EmailLayoutsApi } = await import('../../services/rest/EmailLayouts');
                const json = await EmailLayoutsApi.get(layoutId);
                if (cancelled)
                    return;
                setFullLayout(json?.layout || null);
            } catch (e) {
                console.error('Could not load email layout for preview', e);
                if (!cancelled)
                    setLoadError(e);
            } finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, layoutId]);

    const openPreview = useCallback(() => {
        setPreviewLang(lang || i18n.language || Utils.default_lang());
        setOpen(true);
    }, [i18n.language, lang]);

    const closePreview = useCallback(() => {
        setOpen(false);
        setFullLayout(null);
        setLoadError(null);
    }, []);

    if (!layoutId || !listLayout)
        return null;

    const widgets = layout?.widgets || [];
    const showPreview = open && previewContext && fullLayout && !loading && !loadError;

    return (
        <>
            <Button
                type="button"
                color="link"
                size="sm"
                className="p-0 DefaultLayoutsForm-action-link"
                onClick={openPreview}
            >
                <i className="fa fa-eye" />
                &nbsp;
                {t('settings.default_layouts.preview')}
            </Button>
            {open && (
                <ModalComponent
                    title={t('settings.default_layouts.preview')}
                    onClose={closePreview}
                >
                    <div className="DefaultLayoutsForm-email-preview EmailLayouts">
                        {loading && (
                            <div className="text-muted py-4 text-center">
                                <i className="fa fa-spinner fa-spin" />
                            </div>
                        )}
                        {loadError && !loading && (
                            <div className="text-danger py-3">
                                {t('settings.default_layouts.preview_error')}
                            </div>
                        )}
                        {showPreview && (
                            <>
                                <div className="mb-3">
                                    <LanguageSelector
                                        value={previewLang}
                                        onChange={setPreviewLang}
                                        languages={layoutPreviewLanguages}
                                    />
                                </div>
                                <div className="previewWrapper">
                                    <div className="preview">
                                        <EmailLayoutPreviewChrome
                                            inEdition={false}
                                            setting={previewSetting}
                                            preview_context={previewContext}
                                            lang={previewLang}
                                            subject={layout.subject}
                                            layout_options={layout.options}
                                        />
                                        <div className="contentWrapper" style={contentWrapperStyle}>
                                            <SortableList
                                                layout={layout}
                                                widgets={widgets}
                                                currentWidget={undefined}
                                                inEdition={false}
                                                lang={previewLang}
                                                setting={previewSetting}
                                                preview_context={previewContext}
                                                onClick={() => {}}
                                                onSort={() => {}}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </ModalComponent>
            )}
        </>
    );
};

export default EmailLayoutPreviewButton;
